import { useRef, useState, useCallback } from 'react';
import { flightApiService } from '../services/api';

export const useFlightSearch = () => {
  const searchInProgress = useRef(false);
  const [flightData, setFlightData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [currentFilters, setCurrentFilters] = useState({});
  const [quotaOver, setQuotaOver] = useState(false);

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [switchedToPermanentQuota, setSwitchedToPermanentQuota] = useState(false);
  const [usingPermanentQuota, setUsingPermanentQuota] = useState(false);
  const [previouslyUsingPermanent, setPreviouslyUsingPermanent] = useState(false);
  const [rowsFromExpiring, setRowsFromExpiring] = useState(0);
const [rowsFromPermanent, setRowsFromPermanent] = useState(0);

  // Pagination state
  const [totalRecords, setTotalRecords] = useState(0);
  const [returnedRecords, setReturnedRecords] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLimited, setIsLimited] = useState(false);
  const [limit, setLimit] = useState(100);
  const [loadAll, setLoadAll] = useState(false);
  
  // Download state
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Store search parameters for load all functionality
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [lastSelectedFields, setLastSelectedFields] = useState(null);
  const [lastUserId, setLastUserId] = useState(null);

  // MODIFIED: Added clearingFilters parameter
  const searchFlights = async (userId, searchParams, selectedFields, loadAllRecords = false, filters = {}, clearingFilters = false) => {
    console.log('searchFlights called with:', { 
      userId, 
      searchParams, 
      selectedFields, 
      loadAllRecords, 
      filters,
      clearingFilters,
      existingSessionId: sessionId 
    });
    
    setLoading(true);
    setError(null);
    
    // Store current filters
    setCurrentFilters(filters);
    
    // Store parameters for potential load all call
    if (!loadAllRecords) {
      setLastSearchParams(searchParams);
      setLastSelectedFields(selectedFields);
      setLastUserId(userId);
      setLoadAll(false);
    }
    
    try {
      console.log('Calling flightApiService.searchFlights with sessionId:', sessionId);
      
      // MODIFIED: Pass sessionId in these cases:
      // 1. Load All request
      // 2. Applying filters (filters object has keys)
      // 3. Clearing filters (clearingFilters flag is true)
      // Don't pass sessionId ONLY for initial fresh searches (when sessionId is null)
      const sessionIdToSend = (loadAllRecords || Object.keys(filters).length > 0 || clearingFilters) && sessionId ? sessionId : null;
          // Store previous state before updating
      const wasUsingPermanent = usingPermanentQuota;
      
      console.log('SessionId being sent to API:', sessionIdToSend);
      
      
      // Pass sessionId to the API call
      const results = await flightApiService.searchFlights(
        userId, 
        searchParams, 
        selectedFields, 
        loadAllRecords,
        filters,
        sessionIdToSend
      );
      
      console.log('Received results:', results);
      
      // Store session ID from response
      if (results.sessionId) {
        console.log('Storing new session ID:', results.sessionId);
        setSessionId(results.sessionId);
      }
      
      // Handle the API response structure
      if (results && typeof results === 'object' && 'data' in results) {
        if (results.quotaOver) {
          setFlightData([]);
        } else {
          setFlightData(results.data || []);
        }
        setTotalRecords(results.totalRecords || 0);
        setReturnedRecords(results.returnedRecords || 0);
        setHasMore(results.hasMore || false);
        setQuotaOver(results.quotaOver || false);
        setIsLimited(results.isLimited || false);
        setLimit(results.limit || 0);
        setLoadAll(results.loadAll || false);
        setSwitchedToPermanentQuota(results.switchedToPermanentQuota || false);
        setUsingPermanentQuota(results.usingPermanentQuota || false);
        setRowsFromExpiring(results.rowsFromExpiring || 0);
        setRowsFromPermanent(results.rowsFromPermanent || 0);
        // Detect if user switched back to monthly quota (new month reset)
        if (wasUsingPermanent && !results.usingPermanentQuota) {
          setPreviouslyUsingPermanent(true);
        } else {
          setPreviouslyUsingPermanent(false);
        }
        
        // Set download-related data
        setDownloadUrl(results.downloadUrl || null);
        setFileName(results.fileName || null);
        setErrorMessage(results.errorMessage || null);
      } else {
        // Fallback for array response (backward compatibility)
        console.warn('Received array response, using fallback mode');
        setFlightData(Array.isArray(results) ? results : []);
        setTotalRecords(Array.isArray(results) ? results.length : 0);
        setReturnedRecords(Array.isArray(results) ? results.length : 0);
        setHasMore(false);
        setQuotaOver(false);
        setIsLimited(false);
        setLoadAll(false);
        setSwitchedToPermanentQuota(false);
        setUsingPermanentQuota(false);
        
        // Clear download data for fallback case
        setDownloadUrl(null);
        setFileName(null);
        setErrorMessage(null);
      }
      
      setHasSearched(true);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
      setFlightData([]);
      setTotalRecords(0);
      setReturnedRecords(0);
      setHasMore(false);
      setQuotaOver(false);
      setIsLimited(false);
      setLoadAll(false);
      setSwitchedToPermanentQuota(false);
      setUsingPermanentQuota(false);
      
      // Clear download data on error
      setDownloadUrl(null);
      setFileName(null);
      setErrorMessage(null);
    } finally {
      setLoading(false);
      searchInProgress.current = false;
    }    
  };

  const loadAllRecords = async () => {
    if (!lastUserId || !lastSearchParams || !lastSelectedFields) {
      setError('Cannot load all records: missing search parameters');
      return;
    }
    
    console.log('Loading all records with current filters and sessionId:', { 
      filters: currentFilters, 
      sessionId 
    });
    
    await searchFlights(lastUserId, lastSearchParams, lastSelectedFields, true, currentFilters);
  };

  const resetToLimited = async () => {
    if (!lastUserId || !lastSearchParams || !lastSelectedFields) {
      setError('Cannot reset to limited view: missing search parameters');
      return;
    }
    
    console.log('Resetting to limited view with current filters and sessionId:', { 
      filters: currentFilters, 
      sessionId 
    });
    
    await searchFlights(lastUserId, lastSearchParams, lastSelectedFields, false, currentFilters);
  };

  // Server-side filter handler
  const handleServerFilter = useCallback(async (filters) => {
    console.log('handleServerFilter called with:', { 
      filters, 
      currentFilters, 
      sessionId 
    });
    
    // Check if filters actually changed to prevent unnecessary calls
    if (JSON.stringify(filters) === JSON.stringify(currentFilters)) {
      console.log('Filters unchanged, skipping server filter call');
      return;
    }
    
    if (!lastUserId || !lastSearchParams || !lastSelectedFields) {
      console.warn('Missing search parameters for server filtering');
      return;
    }
    
    // Check if search is in progress
    if (searchInProgress.current) {
      console.log('Search in progress, skipping server filter');
      return;
    }
    
    // Update current filters immediately for UI feedback
    setCurrentFilters(filters);
    
    try {
      await searchFlights(lastUserId, lastSearchParams, lastSelectedFields, loadAll, filters);
      console.log('Server filter applied successfully with session:', sessionId);
    } catch (err) {
      console.error('Server filter error:', err);
      setError(`Filter error: ${err.message}`);
    }
  }, [lastUserId, lastSearchParams, lastSelectedFields, loadAll, currentFilters, sessionId]);

  // Clear filters function - ONLY clears the filters state, doesn't trigger search
  const clearFilters = useCallback(async () => {
    console.log('Clearing filters (state only)');
    setCurrentFilters({});
    console.log('Filters cleared successfully');
  }, []);

  // MODIFIED: Clear filters with search - passes clearingFilters flag
  const clearFiltersWithSearch = useCallback(async () => {
    console.log('Clearing filters with search, sessionId:', sessionId);
    
    if (!lastUserId || !lastSearchParams || !lastSelectedFields) {
      console.warn('Missing search parameters for clearing filters');
      return;
    }
    
    setCurrentFilters({});
    
    try {
      // Pass clearingFilters=true to ensure sessionId is sent even with empty filters
      await searchFlights(lastUserId, lastSearchParams, lastSelectedFields, loadAll, {}, true);
      console.log('Filters cleared with search successfully');
    } catch (err) {
      console.error('Clear filters with search error:', err);
      setError(`Clear filters error: ${err.message}`);
    }
  }, [lastUserId, lastSearchParams, lastSelectedFields, loadAll, sessionId]);

  const resetSearch = () => {
    console.log('Resetting search state and clearing session');
    
    setFlightData([]);
    setHasSearched(false);
    setError(null);
    setTotalRecords(0);
    setReturnedRecords(0);
    setHasMore(false);
    setQuotaOver(false);
    setIsLimited(false);
    setLoadAll(false);
    setLastSearchParams(null);
    setLastSelectedFields(null);
    setLastUserId(null);
    setCurrentFilters({});
    setSessionId(null);
    
    // Clear download data
    setDownloadUrl(null);
    setFileName(null);
    setErrorMessage(null);
  };

  return {
    flightData,
    loading,
    error,
    hasSearched,
    totalRecords,
    returnedRecords,
    hasMore,
    isLimited,
    limit,
    loadAll,
    downloadUrl,
    fileName,
    errorMessage,
    currentFilters,
    quotaOver,
    switchedToPermanentQuota,
    usingPermanentQuota,
    previouslyUsingPermanent,
    rowsFromExpiring,
    rowsFromPermanent,
    sessionId,
    searchFlights,
    loadAllRecords,
    resetToLimited,
    resetSearch,
    handleServerFilter,
    clearFilters,
    clearFiltersWithSearch,
    
  };
};